// src/pages/NewCreditNote.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // ← Added useLocation
import {
    FileText, User, Calendar, MessageSquare, Loader2, Building2, X, Send,
    Search
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import InvoiceItemsTable from '../components/ui/InvoiceItemsTable';
import { useToast } from '../hooks/useToast';
import {
    fetchUtilCustomers,
    fetchStoresWhereStaffAssigned,
    fetchCatentriesUtil,
    readTaxes,
    fetchDiscountRules,
    fetchOrderByStoreAndIdempotency
} from '../apiHelper';
import { client } from '../apiHelper';

const NewCreditNote = () => {
    const navigate = useNavigate();
    const location = useLocation(); // ← For receiving passed invoice
    const { showToast } = useToast();
    const [user, setUser] = useState(null);

    // Extract optional invoice from location.state
    // const passedInvoice = location.state?.invoice || location.state?.invoice_number
    //     ? { idempotency_key: location.state.invoice_number || location.state.invoice?.idempotency_key }
    //     : null;
    const passedInvoiceNumber = location.state?.invoice_number || '';
    const passedStoreId = location.state?.store_id || '';

    const [formData, setFormData] = useState({
        customerId: '',
        storeId: passedStoreId,
        creditNoteNumber: '',
        referenceNumber: passedInvoiceNumber || '', // ← Pre-fill if passed
        creditNoteDate: new Date().toISOString().slice(0, 16),
        subject: passedInvoiceNumber ? `Credit for Invoice #${passedInvoiceNumber}` : '',
        creditNoteStatus: 'PRODUCT_RETURN',
    });

    const [lineItems, setLineItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dropdown options
    const [customerOptions, setCustomerOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);
    const [taxOptions, setTaxOptions] = useState([]);
    const [discountOptions, setDiscountOptions] = useState([]);

    // Loading states
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingStores, setLoadingStores] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingTaxes, setLoadingTaxes] = useState(true);
    const [loadingDiscounts, setLoadingDiscounts] = useState(true);
    const [isPopulatingOrder, setIsPopulatingOrder] = useState(false);

    const [populatedItems, setPopulatedItems] = useState(null);

    // Load user
    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) {
            setUser(JSON.parse(stored));
        } else {
            showToast('Please log in to create credit notes.', 'info');
            navigate('/login');
        }
    }, [showToast, navigate]);

    // Fetch Customers
    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user) return;
        setLoadingCustomers(true);
        try {
            const customers = await fetchUtilCustomers(user, searchQuery);
            setCustomerOptions(customers.map(c => {
                const u = c.user || {};
                const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || 'Unnamed Customer';
                const logonId = u.credential?.logon_id || 'No ID';
                return {
                    label: `${fullName} (${logonId})`,
                    value: c.member_id,
                    customerData: c
                };
            }));
        } catch (err) {
            showToast('Failed to load customers', 'error');
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, showToast]);

    useEffect(() => {
        if (user) fetchCustomers();
    }, [user, fetchCustomers]);

    // Fetch Stores
    useEffect(() => {
        if (!user) return;
        const loadStores = async () => {
            setLoadingStores(true);
            try {
                const stores = await fetchStoresWhereStaffAssigned(user);
                setStoreOptions(stores.map(store => ({
                    label: store.identifier || `Branch ${store.store_id}`,
                    value: store.store_id,
                    storeData: store
                })));
            } catch (err) {
                showToast('Failed to load branches', 'error');
            } finally {
                setLoadingStores(false);
            }
        };
        loadStores();
    }, [user, showToast]);

    // Fetch Items, Taxes, Discounts (same as invoice)
    useEffect(() => {
        if (!user) return;
        const load = async () => {
            setLoadingItems(true);
            try {
                const items = await fetchCatentriesUtil(user);
                setItemOptions(items.map(i => ({
                    value: i.catentry_id,
                    label: i.productname,
                    price: parseFloat(i.listprice),
                    currency: i.currency,
                })));
            } catch { showToast('Failed to load items', 'error'); } finally { setLoadingItems(false); }
        };
        load();
    }, [user, showToast]);

    useEffect(() => {
        if (!user) return;
        const load = async () => {
            setLoadingTaxes(true);
            try {
                const taxes = await readTaxes(user);
                setTaxOptions(taxes.map(t => ({
                    label: `${t.code} – ${t.identifier} (${t.rate.toFixed(1)}%)`,
                    value: t.taxcategory_id.toString(),
                    rate: parseFloat(t.rate),
                })));
            } catch { showToast('Failed to load taxes', 'error'); } finally { setLoadingTaxes(false); }
        };
        load();
    }, [user, showToast]);

    // LOAD DISCOUNTS USEEFFECT
    useEffect(() => {
        if (!user) return;
        const load = async () => {
            setLoadingDiscounts(true);
            try {
                const rules = await fetchDiscountRules({ ...user, offset: 0, limit: 1000 });
                // console.log(rules)
                setDiscountOptions(rules.map(r => ({
                    label: `${r.name} – ${r.description}`,
                    value: r.discountrule_id.toString(),
                    type: r.discount_type,
                    valueAmount: parseFloat(r.value),

                    isActive: r.is_active || r.status === 'active' || true,
                    startDate: r.start_date,
                    endDate: r.end_date,
                    appliesTo: r.applies_to || 'order' || 'product',
                    targetProductId: r.target_product_id,
                    customers: Array.isArray(r.customers) ? r.customers.map(cust => Number(cust.member_id || cust.id)).filter(id => !isNaN(id)) : (r.customer_ids || []),
                    stores: Array.isArray(r.stores) ? r.stores.map(store => Number(store.store_id)).filter(id => !isNaN(id)) : [],
                    minimumPurchaseQuantity: parseFloat(r.minimum_purchase_quantity) || 0,
                    minimumPurchaseAmount: parseFloat(r.minimum_purchase_amount) || 0,
                })));
            } catch (err) {
                showToast('Failed to load discounts', 'error');
                console.error(err)
            } finally {
                setLoadingDiscounts(false);
            }
        };
        load();
    }, [user, showToast]);

    useEffect(() => {
        if (!formData.subject.includes('Credit for Invoice')) return; // only override if default

        const prefix = formData.creditNoteStatus === 'PRODUCT_RETURN'
            ? 'Return of goods - '
            : 'Billing correction - ';

        setFormData(prev => ({
            ...prev,
            subject: `${prefix}Invoice #${formData.referenceNumber}`
        }));
    }, [formData.creditNoteStatus, formData.referenceNumber]);

    const handlePopulateFromInvoice = useCallback(async () => {
        const invoiceId = formData.referenceNumber.trim();
        if (!invoiceId || !formData.storeId || !user?.users_id) {
            showToast("Please enter invoice ID and select branch.", "error");
            return;
        }

        setIsPopulatingOrder(true);
        try {
            const payload = {
                store_id: Number(formData.storeId),
                idempotency_key: invoiceId,
            };

            console.log("Fetching invoice for credit note:", payload);
            const invoiceData = await fetchOrderByStoreAndIdempotency(payload);
            console.log(invoiceData)

            if (!invoiceData) {
                showToast("Invoice not found.", "error");
                return;
            }

            // console.log("Invoice loaded successfully:", invoiceData);

            // 1. Set Customer
            if (invoiceData.customer?.member_id) {
                const cust = invoiceData.customer;
                const fullName = `${cust.user?.first_name || ''} ${cust.user?.last_name || ''}`.trim();
                const logonId = cust.user?.credential?.logon_id || 'N/A';

                const customerOption = {
                    label: `${fullName} (${logonId})`,
                    value: cust.member_id,
                    customerData: cust
                };

                console.log(customerOption)

                // Update customer options if not already present
                setCustomerOptions(prev => {
                    const exists = prev.some(opt => opt.value === cust.member_id);
                    return exists ? prev : [...prev, customerOption];
                });

                // Set selected customer in form
                setFormData(prev => ({
                    ...prev,
                    customerId: cust.member_id
                }));
            }

            // 2. Set Currency
            const currency = invoiceData.currency || invoiceData.order_currency?.setccurr || 'NGN';
            setFormData(prev => ({ ...prev, currency }));

            // 3. Set Subject
            const subject = `Credit Note for Invoice #${invoiceData.idempotency_key}`;
            setFormData(prev => ({ ...prev, subject }));

            // 4. Populate Line Items
            if (Array.isArray(invoiceData.orderitems) && invoiceData.orderitems.length > 0) {

                // We map the API response to the Table's intenal structure
                const items = invoiceData.orderitems.map((item, index) => {
                    const price = parseFloat(item.price || 0);
                    const qty = parseFloat(item.quantity || 1);

                    const calculatedAmount = (price * qty).toFixed(2);

                    return {
                        id: Date.now() + index,
                        itemId: item.catentry_id,
                        quantity: qty,
                        rate: price.toFixed(2),
                        amount: calculatedAmount,
                        discountId: '', // You can add logic here to map discounts if needed
                        taxId: '', // You can add logic here to map taxes if needed
                    }
                })

                console.log("Line items populated:", items);
                setPopulatedItems(items)
                // setLineItems(items);
                // remove setLineItems(items) here!
                // why? because the child will receive 'populatedItems', update itself, 
                // and then callback 'onItemsChange', which will update lineItems automatically.
            }

            // // 5. Calculate Gross (before discount)
            // const gross = invoiceData.orderitems.reduce((sum, item) => {
            //     return sum + (parseFloat(item.price) * item.quantity);
            // }, 0);

            // const totalDiscount = parseFloat(invoiceData.totaldiscount || 0);
            // const totalTax = parseFloat(invoiceData.totaltax || 0);

            // console.log("Gross (before discount):", gross);
            // console.log("Total Discount:", totalDiscount);
            // console.log("Total Tax:", totalTax);

            // // 6. Calculate Discount Percentage
            // let discountPercentage = 0;
            // if (gross > 0 && totalDiscount > 0) {
            //     discountPercentage = (totalDiscount / gross) * 100;
            //     console.log("Calculated Discount %:", discountPercentage.toFixed(4));
            // }

            // // 7. Calculate Tax Rate Percentage
            // let taxRate = 0;
            // if (gross > totalDiscount && totalTax > 0) {
            //     const taxableAmount = gross - totalDiscount;
            //     taxRate = (totalTax / taxableAmount) * 100;
            //     console.log("Calculated Tax Rate %:", taxRate.toFixed(4));
            // }

            // // 8. Auto-select Discount Rule (by matching valueAmount)
            // if (discountPercentage > 0) {
            //     const matchedDiscount = discountOptions.find(rule =>
            //         Math.abs(rule.valueAmount - discountPercentage) < 0.01 && // tolerance
            //         rule.type === 'percentage'
            //     );

            //     if (matchedDiscount) {
            //         console.log("Auto-selected Discount Rule:", matchedDiscount);
            //         // Apply to all line items or globally — depending on your table logic
            //         // Here we assume global — you may want to set a state or apply per line
            //         showToast(`Applied discount rule: ${matchedDiscount.label}`, "info");
            //     }
            // }

            // // 9. Auto-select Tax Category (by matching rate)
            // if (taxRate > 0) {
            //     const matchedTax = taxOptions.find(tax =>
            //         Math.abs(tax.rate - taxRate) < 0.01
            //     );

            //     if (matchedTax) {
            //         console.log("Auto-selected Tax Category:", matchedTax);
            //         showToast(`Applied tax: ${matchedTax.label}`, "info");
            //     }
            // }

            showToast(`Invoice #${invoiceId} loaded successfully!`, "success");

        } catch (err) {
            const errorMsg = err.response?.data?.detail || "Failed to load invoice.";
            showToast(errorMsg, "error");
            console.error("Populate invoice error:", err);
        } finally {
            setIsPopulatingOrder(false);
        }
    }, [
        formData.referenceNumber,
        formData.storeId,
        user?.users_id,
        showToast,
        discountOptions,
        taxOptions
    ]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemsChange = (items) => setLineItems(items);

    const validateForm = () => {
        if (!formData.customerId) return 'Customer is required.';
        if (!formData.storeId) return 'Branch is required.';
        if (!formData.referenceNumber.trim()) return 'Reference Invoice # is required.';
        if (lineItems.length === 0) return 'Please add at least one item.';
        return null;
    };

    // const handleSave = async () => {
    //     const error = validateForm();
    //     if (error) return showToast(error, 'error');
    //     setIsSubmitting(true);
    //     try {
    //         const payload = {
    //             ...formData,
    //             customer_id: Number(formData.customerId),
    //             store_id: Number(formData.storeId),
    //             attendant_id: user.users_id,
    //             credit_note_number: formData.creditNoteNumber || `CN-${Date.now()}`,
    //             reference_invoice: formData.referenceNumber.trim(),
    //             items: lineItems.map(item => ({
    //                 product_id: item.itemId ? Number(item.itemId) : null,
    //                 quantity: parseFloat(item.quantity || 1),
    //                 unit_price: parseFloat(item.rate || 0),
    //                 subtotal: Number((parseFloat(item.rate || 0) * parseFloat(item.quantity || 1)).toFixed(2)),
    //             })),
    //             total_amount: lineItems.reduce((sum, i) => sum + (parseFloat(i.rate || 0) * parseFloat(i.quantity || 1)), 0).toFixed(2),
    //         };

    //         console.log(payload)

    //         // const response = await client.postData('/sales/create-credit-note', payload);
    //         // showToast(response.msg || 'Credit note created successfully!', 'success');
    //         // navigate('/sales/credit-notes');
    //     } catch (err) {
    //         showToast(err.response?.data?.msg || 'Failed to create credit note.', 'error');
    //     } finally {
    //         setIsSubmitting(false);
    //     }
    // };
    const handleSave = async () => {
        const error = validateForm();
        if (error) return showToast(error, 'error');

        setIsSubmitting(true);

        try {
            // 1. First, let the table calculate final amounts (it already does!)
            //    → lineItems contains correct .amount (after discount + tax)

            const itemsPayload = lineItems.map(item => {
                const qty = parseFloat(item.quantity || 1);
                const rate = parseFloat(item.rate || 0);
                const gross = qty * rate; // before any discount/tax

                // Re-run the same logic as InvoiceItemRow (or trust it already did)
                const selectedDiscount = discountOptions.find(d => d.value === item.discountId);
                let discountAmount = 0;
                if (selectedDiscount?.isActive) {
                    if (selectedDiscount.type === 'percentage') {
                        discountAmount = gross * (selectedDiscount.valueAmount / 100);
                    } else if (selectedDiscount.type === 'fixed') {
                        discountAmount = selectedDiscount.valueAmount;
                    }
                }

                const taxable = gross - discountAmount;

                const selectedTax = taxOptions.find(t => t.value === item.taxId);
                const taxRate = (selectedTax?.rate || 0) / 100;
                const taxAmount = taxable * taxRate;

                const lineTotal = taxable + taxAmount;

                return {
                    product_id: Number(item.itemId),
                    quantity: qty,
                    unit_price: rate,
                    subtotal: Number(taxable.toFixed(2)),        // ← after discount, before tax
                    discount_amount: Number(discountAmount.toFixed(2)),
                    tax_amount: Number(taxAmount.toFixed(2)),
                    total: Number(lineTotal.toFixed(2)),         // ← final line total
                };
            });

            // 2. Calculate totals
            const subtotal = itemsPayload.reduce((sum, i) => sum + i.subtotal, 0);
            const discount_total = itemsPayload.reduce((sum, i) => sum + i.discount_amount, 0);
            const tax_total = itemsPayload.reduce((sum, i) => sum + i.tax_amount, 0);
            const total = subtotal + tax_total;

            const payload = {
                customer_id: Number(formData.customerId),
                store_id: Number(formData.storeId),
                sales_person_id: user.users_id,                    // ← FIXED
                credit_note_number: formData.creditNoteNumber || `CN-${Date.now()}`,
                reference_invoice: formData.referenceNumber.trim(),
                credit_note_date: formData.creditNoteDate,
                subject: formData.subject || "Credit Note",

                // ← REQUIRED STATUS
                status: formData.creditNoteStatus,

                items: itemsPayload,

                // ← TOTALS (REQUIRED BY BACKEND)
                subtotal: Number(subtotal.toFixed(2)),
                discount_total: Number(discount_total.toFixed(2)),
                tax_total: Number(tax_total.toFixed(2)),
                total: Number(total.toFixed(2)),
            };

            console.log("Credit Note Payload →", payload); // ← Debug this!

            const response = await client.postData('/sales/create-credit-note', payload);

            showToast(response.msg || 'Credit note created successfully!', 'success');
            navigate('/sales/credit-notes');
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.msg || 'Failed to create credit note.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!user) {
        return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin h-10 w-10 text-gmail-blue" /></div>;
    }

    return (
        <div className="w-full mx-auto p-4 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                <div className="lg:col-span-2 space-y-6">

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                                <FileText className="text-gmail-blue" /> New Credit Note
                            </h1>
                            <p className="text-text-secondary text-sm">Issue a credit against an existing invoice.</p>
                        </div>
                    </div>

                    {/* Customer + Branch in Same Section */}
                    <Card className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <SearchableSelectBox
                                name="customerId"
                                label="Customer Name"
                                required
                                value={formData.customerId}
                                onChange={handleInputChange}
                                options={customerOptions}
                                onSearch={fetchCustomers}
                                loading={loadingCustomers}
                                placeholder="Search customers..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            <SearchableSelectBox
                                name="storeId"
                                label="Branch"
                                required
                                value={formData.storeId}
                                onChange={handleInputChange}
                                options={storeOptions}
                                loading={loadingStores}
                                placeholder="Select branch..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                icon={<Building2 size={18} className="text-gray-500" />}
                            />
                        </div>
                    </Card>

                    {/* Credit Note Details */}
                    <Card className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Input
                                name="creditNoteNumber"
                                label="Credit Note #"
                                placeholder="(Auto-generated)"
                                value={formData.creditNoteNumber}
                                onChange={handleInputChange}
                                icon={<span className="text-gray-400 font-bold">#</span>}
                            />
                            <div className="space-y-1">
                                <Input
                                    name="referenceNumber"
                                    label="Reference Invoice #"
                                    required
                                    placeholder="e.g. 780B5CCCEF2347C"
                                    value={formData.referenceNumber}
                                    onChange={handleInputChange}
                                />

                                {/* Always visible "Populate" link — matches CustomerReturns exactly */}
                                <button
                                    type="button"
                                    onClick={handlePopulateFromInvoice}
                                    disabled={isPopulatingOrder || !formData.storeId}
                                    className={`
      text-sm font-medium text-gmail-blue hover:text-gmail-blue/80 
      disabled:text-gray-400 disabled:cursor-not-allowed
      underline underline-offset-2 transition-colors flex items-center gap-1
      ${isPopulatingOrder ? 'animate-pulse' : ''}
    `}
                                >
                                    {isPopulatingOrder ? (
                                        <>Populating...</>
                                    ) : (
                                        <>
                                            <Search size={14} />
                                            Populate using this invoice ID
                                        </>
                                    )}
                                </button>
                            </div>
                            <Input
                                type="datetime-local"
                                name="creditNoteDate"
                                label="Credit Note Date & Time"
                                required
                                value={formData.creditNoteDate}
                                onChange={handleInputChange}
                                step="60"
                            />
                        </div>

                        <div className="flex items-center gap-2 mb-4 text-text-secondary">
                            <MessageSquare size={18} />
                            <h3 className="font-semibold text-sm uppercase tracking-wide">Reason</h3>
                        </div>
                        <Input
                            name="subject"
                            label="Subject"
                            placeholder="e.g. Return of damaged goods"
                            value={formData.subject}
                            onChange={handleInputChange}
                        />
                        <div className="flex items-center gap-2 mb-4 text-text-secondary">
                            <MessageSquare size={18} />
                            <h3 className="font-semibold text-sm uppercase tracking-wide">Credit Reason</h3>
                        </div>

                        <div className="space-y-4 p-5 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="space-y-3">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="creditNoteStatus"
                                        value="PRODUCT_RETURN"
                                        checked={formData.creditNoteStatus === 'PRODUCT_RETURN'}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 text-gmail-blue focus:ring-gmail-blue"
                                    />
                                    <div>
                                        <div className="font-medium text-text-primary">Product Return</div>
                                        <div className="text-xs text-text-secondary">Customer returned damaged/defective goods</div>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="creditNoteStatus"
                                        value="BILLING_ERROR"
                                        checked={formData.creditNoteStatus === 'BILLING_ERROR'}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 text-gmail-blue focus:ring-gmail-blue"
                                    />
                                    <div>
                                        <div className="font-medium text-text-primary">Billing Error / Overcharge</div>
                                        <div className="text-xs text-text-secondary">Incorrect pricing, damage, duplicate charge, or wrong item billed</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </Card>

                    {/* Line Items */}
                    <div className="space-y-2">
                        <h3 className="font-semibold text-text-primary">Items to Credit</h3>
                        {loadingItems || loadingTaxes || loadingDiscounts ? (
                            <Card className="p-12 text-center"><Loader2 className="animate-spin h-10 w-10 mx-auto text-gmail-blue" /></Card>
                        ) : (
                            <InvoiceItemsTable
                                itemOptions={itemOptions}
                                taxOptions={taxOptions}
                                discountOptions={discountOptions}
                                currencySymbol="₦"
                                onItemsChange={handleItemsChange}
                                customerId={formData.customerId}
                                storeId={formData.storeId}
                                allItems={lineItems}
                                overrideItems={populatedItems}
                            />
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-col-reverse md:flex-row justify-end gap-4 pt-6 border-t">
                        <Button className="flex items-center justify-cemter py-4" variant="secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>
                            <X size={18} className="mr-2" /> Cancel
                        </Button>
                        <Button className="flex items-center justify-cemter py-4" variant="primary" onClick={handleSave} disabled={isSubmitting}>
                            <Send size={18} className="mr-2" />
                            {isSubmitting ? 'Creating...' : 'Create Credit Note'}
                        </Button>
                    </div>

                </div>

                <div className="lg:col-span-1">
                    <Card className="p-6 sticky top-6 text-center text-text-secondary">
                        <FileText size={56} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-sm">This credit note will reduce the customer's outstanding balance.</p>
                    </Card>
                </div>

            </div>
        </div>
    );
};

export default NewCreditNote;